// Camera vs furniture gestures in the room.
//
// One finger on an item moves the item; a second finger turns the gesture into a
// camera gesture — never a furniture move. Two fingers pan the view, and only
// spreading/pinching them zooms (PanFirstPinch).
//
// For that the viewport must keep tracking touches while an item is dragged: a
// furniture drag holds only the camera's pan plugins (holdCameraForDrag) instead of
// pausing the whole viewport, so a second finger still reaches its pinch plugin.
import { Pinch, type Viewport } from 'pixi-viewport';
import type { FederatedPointerEvent, Sprite } from 'pixi.js';
import { asFurnitureSprite } from '../render/pixiExtensions';
import { applyWallArtFacing } from '../render/wallArtFacing';

/** Plugins that would pan (or fling) the camera under a finger dragging an item. */
const PAN_PLUGINS = ['drag', 'decelerate'] as const;

/**
 * Stop the camera following the finger that drags an item. Returns the release:
 * it resumes only what this call paused (the admin tile editor may hold 'drag' too).
 */
export function holdCameraForDrag(vp: Viewport | null | undefined): () => void {
  if (!vp) return () => {};
  const held = PAN_PLUGINS.filter((name) => {
    const plugin = vp.plugins.get(name, true);
    if (!plugin || plugin.paused) return false;
    vp.plugins.pause(name);
    return true;
  });
  return () => held.forEach((name) => vp.plugins.resume(name));
}

/**
 * Another finger is already on the screen: the browser marks every touch that
 * starts while one is active as non-primary. (The viewport's own touch list is not
 * used — it misses releases while the viewport is paused, e.g. under a menu.)
 */
export function isExtraFinger(event: FederatedPointerEvent): boolean {
  return event.pointerType !== 'mouse' && !event.isPrimary;
}

/**
 * Turn a dragged item back to the facing it had when the drag began (a wall item
 * flips to face the wall it is dragged onto; a cancelled drag must undo that).
 */
export function restoreFacing(sprite: Sprite, mirrored: boolean): void {
  if ((sprite.scale.x < 0) === mirrored) return;
  sprite.scale.x = mirrored ? -Math.abs(sprite.scale.x) : Math.abs(sprite.scale.x);
  const fs = asFurnitureSprite(sprite);
  if (!fs) return;
  applyWallArtFacing(fs);
  const base = fs.__visualBaseOffsetX ?? 0;
  sprite.x = mirrored ? -base : base;
  if (fs.furnitureData) fs.furnitureData = { ...fs.furnitureData, is_mirrored: mirrored };
}

/** Spread/pinch (screen px) the two fingers must make before the view zooms. */
export const PINCH_ZOOM_DEADBAND_PX = 24;

/**
 * Pinch that pans first: two fingers moving together only move the view; the zoom
 * starts once the distance between them has changed by PINCH_ZOOM_DEADBAND_PX, and
 * then follows smoothly from there (pixi-viewport zooms by the change since the
 * last move, so holding the zoom at 0 until then causes no jump).
 */
export class PanFirstPinch extends Pinch {
  private startDistance: number | null = null;

  constructor(parent: Viewport) {
    super(parent, { percent: 0 });
  }

  move(e: FederatedPointerEvent): boolean {
    const [a, b] = this.parent.input.touches;
    if (this.active && a?.last && b?.last) {
      // touch.last is in the viewport's parent space = screen px.
      const distance = Math.hypot(b.last.x - a.last.x, b.last.y - a.last.y);
      this.startDistance ??= distance;
      if (this.options.percent === 0 && Math.abs(distance - this.startDistance) >= PINCH_ZOOM_DEADBAND_PX) {
        (this.options as { percent: number }).percent = 1;
      }
    }
    return super.move(e);
  }

  up(): boolean {
    const ended = super.up();
    if (ended || !this.active) {
      this.startDistance = null;
      (this.options as { percent: number }).percent = 0;
    }
    return ended;
  }
}
